import { useState, useEffect } from "react";
import { Plus, ListMusic, Check, Loader2, Sparkles } from "lucide-react";
import { usePlaylists } from "@/hooks/usePlaylists";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
  RetroDialogFooter,
} from "@/components/ui/retro-dialog";

interface AddToPlaylistProps {
  open: boolean;
  onClose: () => void;
  episodeId: string;
  searchQuery?: string;
}

export default function AddToPlaylist({ open, onClose, episodeId, searchQuery }: AddToPlaylistProps) {
  const { playlists, loading, createPlaylist, addEpisode, removeEpisode } = usePlaylists();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [addedTo, setAddedTo] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // Auto-generate title + description when opening create form with a search query
  useEffect(() => {
    if (!creating || !searchQuery?.trim()) return;
    let cancelled = false;

    const generate = async () => {
      setGenerating(true);
      try {
        const { data, error } = await supabase.functions.invoke("generate-playlist-meta", {
          body: { searchQuery },
        });
        if (!cancelled && data && !error) {
          setNewTitle(data.title || "");
          setNewDescription(data.description || "");
        }
      } catch (e) {
        console.error("Failed to generate playlist meta:", e);
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  }, [creating, searchQuery]);

  const handleToggle = async (playlistId: string) => {
    const isAdded = addedTo.has(playlistId);
    setAdding(playlistId);
    if (isAdded) {
      await removeEpisode(playlistId, episodeId);
      setAddedTo((prev) => {
        const next = new Set(prev);
        next.delete(playlistId);
        return next;
      });
      toast.success("Episode removed from playlist");
    } else {
      const ok = await addEpisode(playlistId, episodeId);
      if (ok) {
        setAddedTo((prev) => new Set(prev).add(playlistId));
        toast.success("Episode added to playlist");
      } else {
        toast.info("Already in this playlist");
        setAddedTo((prev) => new Set(prev).add(playlistId));
      }
    }
    setAdding(null);
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    const pl = await createPlaylist(newTitle.trim(), newDescription.trim() || undefined);
    if (pl) {
      await addEpisode(pl.id, episodeId);
      setAddedTo((prev) => new Set(prev).add(pl.id));
      setNewTitle("");
      setNewDescription("");
      setCreating(false);
      toast.success(`Created "${pl.title}" and added episode`);
    }
  };

  const handleStartCreating = () => {
    setCreating(true);
    if (!searchQuery?.trim()) {
      setNewTitle("");
      setNewDescription("");
    }
  };

  return (
    <RetroDialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
      <RetroDialogHeader
        title={
          <span className="flex items-center gap-2">
            <ListMusic className="w-5 h-5 text-primary" />
            Add to Playlist
          </span>
        }
      />
      <RetroDialogBody>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {playlists.map((pl) => {
              const added = addedTo.has(pl.id);
              return (
                <button
                  key={pl.id}
                  onClick={() => adding !== pl.id && handleToggle(pl.id)}
                  disabled={adding === pl.id}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 font-mono text-sm rounded-md transition-colors ${
                    added ? "bg-primary/10 text-primary" : "hover:bg-muted"
                  }`}
                >
                  {adding === pl.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : added ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Plus className="w-3.5 h-3.5" />
                  )}
                  <span className="truncate flex-1">{pl.title}</span>
                  <span className="text-[10px] text-muted-foreground">{pl.item_count}</span>
                </button>
              );
            })}

            {playlists.length === 0 && !creating && (
              <p className="font-mono text-xs text-muted-foreground text-center py-3">
                No playlists yet. Create one below.
              </p>
            )}
          </div>
        )}

        <div className="mt-4 border-t-2 border-foreground/20 pt-4">
          {creating ? (
            <div className="space-y-2">
              {generating && (
                <div className="flex items-center gap-2 text-primary font-mono text-[11px] mb-1">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                  Generating title & description…
                </div>
              )}
              <input
                autoFocus={!searchQuery}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !generating && handleCreate()}
                placeholder={generating ? "Generating…" : "Playlist name…"}
                className="input-retro"
              />
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder={generating ? "Generating…" : "Description (optional)…"}
                rows={2}
                className="textarea-retro min-h-[60px]"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={!newTitle.trim() || generating}
                  className="btn-comic-primary btn-comic-sm disabled:opacity-50"
                >
                  {generating && <Loader2 className="w-3 h-3 animate-spin" />}
                  Create
                </button>
                <button
                  onClick={() => { setCreating(false); setNewTitle(""); setNewDescription(""); }}
                  className="btn-comic-ghost btn-comic-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleStartCreating}
              className="btn-comic-secondary btn-comic-sm w-full"
            >
              <Plus className="w-4 h-4" /> New Playlist
            </button>
          )}
        </div>
      </RetroDialogBody>

      {addedTo.size > 0 && !creating && (
        <RetroDialogFooter>
          <button onClick={onClose} className="btn-comic-primary">
            Done
          </button>
        </RetroDialogFooter>
      )}
    </RetroDialog>
  );
}
